import React, { type HTMLAttributes, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Popover from '@amzn/meridian/popover';
import { type PopoverProps } from '@amzn/meridian/popover/popover';

// **** Types **** //

interface HoverPopoverProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  tag?: React.ElementType;
  title: ReactNode;
  popoverProps?: Omit<PopoverProps, 'open' | 'anchorNode' | 'children' | 'onClose' | 'position'>;
  position?: PopoverProps['position'];
  children: ReactNode;
  // Delay in ms before closing, allows moving into popover. Default 100ms.
  // Really useful when needing to modify CSS properties during development
  closeDelay?: number;
}

// **** Components **** //

/**
 * Shows a <Popover /> when hovering or focusing an element.
 *
 * @param tag - (default 'span') Which element to render.
 * @param title - Content shown in the Popover.
 * @param popoverProps - Additional props forwarded to <Popover />.
 * @param position - (default 'top') Popover placement shortcut.
 * @param closeDelay - (default 0) ms to wait before closing on mouse leave.
 */
const HoverPopover = ({
  tag: Tag = 'span',
  children,
  title,
  popoverProps,
  position = 'top',
  closeDelay = 0,
  ...otherProps
}: HoverPopoverProps) => {
  const [anchorNode, setAnchorNode] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setIsOpen(false), closeDelay);
  }, [closeDelay]);

  // cleanup
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  return (
    <>
      <Tag ref={setAnchorNode} onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close} {...otherProps}>
        {children}
      </Tag>
      <Popover
        open={isOpen && anchorNode !== null}
        anchorNode={anchorNode ?? undefined}
        onClose={() => setIsOpen(false)}
        position={position}
        {...popoverProps}
      >
        {title}
      </Popover>
    </>
  );
};

// **** Export **** //

export default HoverPopover;
